import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  IconButton,
  Alert,
} from "@mui/material";
import { useState, useEffect } from "react";
import { useWebSocketContext } from "components/context/SocketConsumerProvider";
import {
  useStartAgent,
  useAgentMessage,
  useGetAgentsThread,
  useInterruptAgentThread,
} from "services/agents";
import {
  useTaskAgentState,
  processEventsWithAddedData,
} from "components/hooks/useTaskAgentState";
import { GetAgentsThreadRequest } from "protogen/agents_service_pb";
import { RichContent } from "components/editor/utils";
import { ArrowUp, X } from "lucide-react";
import TaskAgentDisplay from "./TaskAgentDisplay";
import Loading from "components/common/Loading";
import FamilySelect from "components/creation/FamilySelect";
import TextField from "components/common/TextField";

interface PlaybookTestDialogProps {
  open: boolean;
  onClose: () => void;
  initialFamilyRef?: string;
  playbookRef: string;
}

export default ({
  open,
  onClose,
  initialFamilyRef,
  playbookRef,
}: PlaybookTestDialogProps) => {
  const [description, setDescription] = useState<string>("");
  const [selectedFamily, setSelectedFamily] = useState<string | null>(
    initialFamilyRef || null,
  );
  const [threadRef, setThreadRef] = useState<string | null>(null);
  const [isInterrupting, setIsInterrupting] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [hasActivated, setHasActivated] = useState(false);
  const [currentThreadRef, setCurrentThreadRef] = useState<string | null>(null);

  const { registerHandler, unregisterHandler } = useWebSocketContext();
  const { request, loading, error } = useStartAgent();
  const { request: agentMessage } = useAgentMessage();
  const stateManager = useTaskAgentState();
  const { request: getAgentsThread } = useGetAgentsThread();
  const { request: interruptAgentThread } = useInterruptAgentThread();

  useEffect(() => {
    if (threadRef) {
      const channel = `task-agent/${encodeURIComponent(threadRef)}`;
      registerHandler(channel, stateManager.actions.handleWebSocketMessage);

      return () => {
        unregisterHandler(channel, stateManager.actions.handleWebSocketMessage);
      };
    }
  }, [
    registerHandler,
    unregisterHandler,
    stateManager.actions.handleWebSocketMessage,
    threadRef,
  ]);

  useEffect(() => {
    if (stateManager.state.isActive) {
      setHasActivated(true);
    }
  }, [stateManager.state.isActive]);

  useEffect(() => {
    if (!open) {
      setDescription("");
      setSelectedFamily(initialFamilyRef || null);
      setThreadRef(null);
      setHasStarted(false);
      setHasActivated(false);
      setCurrentThreadRef(null);
      stateManager.actions.resetState();
    }
  }, [open, initialFamilyRef]);

  const onSubmit = async () => {
    if (!description.trim() || !selectedFamily) {
      return;
    }
    setHasStarted(true);

    const message = `Help me solve this task: ${description}`;

    const response = await request({
      agentType: "adhoc",
      arguments: [
        {
          name: "familyRef",
          value: selectedFamily,
        },
        {
          name: "message",
          value: message,
        },
        {
          name: "playbookRef",
          value: playbookRef,
        },
      ],
      model: "gpt-5-mini",
      // model: "",
    });
    if (response?.threadRef) {
      setCurrentThreadRef(response.threadRef);
      setThreadRef(response.threadRef);
    }
  };

  const handleUpdatePlanDetails = async (content: RichContent) => {
    if (!currentThreadRef) {
      console.error("No thread reference available");
      return;
    }

    // Interrupt the agent first if it's running
    if (stateManager.state.isActive) {
      await handleInterrupt();
      // Wait for interrupt to complete and pending function calls to be cancelled
      await new Promise((resolve) => setTimeout(resolve, 1500));
    }

    // Mark agent as active so it processes the new information
    stateManager.actions.updateState({
      isActive: true,
      lastEventTime: Date.now(),
    });

    // Send plan details to backend WITHOUT showing in chat
    await agentMessage({
      threadRef: currentThreadRef,
      body: {
        contentType: "html",
        payload: content.html,
        textContent: content.text,
        attachments: content.attachments,
      },
      model: "gpt-5-mini",
      hideFromChat: true,
    });
  };

  const handleFollowUpQuestion = async (content: RichContent) => {
    if (!currentThreadRef) {
      console.error("No thread reference available");
      return;
    }
    stateManager.actions.updateState({
      chatMessages: [
        ...stateManager.state.chatMessages,
        { content: content.text, source: "user", contentHtml: content.html },
      ],
      isActive: true,
      lastEventTime: Date.now(),
    });

    await agentMessage({
      threadRef: currentThreadRef,
      body: {
        contentType: "html",
        payload: content.html,
        textContent: content.text,
        attachments: content.attachments,
      },
      model: "gpt-5-mini",
      hideFromChat: false,
    });
  };

  const handleInterrupt = async () => {
    if (!currentThreadRef || isInterrupting) {
      return;
    }

    setIsInterrupting(true);

    stateManager.actions.updateState({
      isActive: false,
      hasCompleted: true,
    });

    await new Promise((resolve) => setTimeout(resolve, 800));

    await interruptAgentThread({
      threadRef: currentThreadRef,
    });

    const response = await getAgentsThread(
      new GetAgentsThreadRequest({ threadRef: currentThreadRef }),
    );

    if (response?.events) {
      const addedEventsMap = new Map<
        number,
        {
          eventRef: string;
          eventStatus: string;
          eventModified: boolean;
          sourceEntityType: string;
          sourceEntityId: string;
          sourceEntityIndex: number;
        }
      >();
      if (response.addedEvents && response.addedEvents.length > 0) {
        response.addedEvents.forEach((addedEvent) => {
          addedEventsMap.set(addedEvent.sourceEntityIndex, {
            eventRef: addedEvent.eventRef,
            eventStatus: addedEvent.eventStatus,
            eventModified: addedEvent.eventModified,
            sourceEntityType: addedEvent.sourceEntityType,
            sourceEntityId: addedEvent.sourceEntityId,
            sourceEntityIndex: addedEvent.sourceEntityIndex,
          });
        });
      }

      stateManager.actions.resetState();

      const processedEvents = processEventsWithAddedData(
        response.events || [],
        addedEventsMap,
      );
      processedEvents.forEach((eventString: string) => {
        const event = JSON.parse(eventString);
        stateManager.actions.handleWebSocketMessage("", event);
      });

      stateManager.actions.updateState({
        isActive: false,
        hasCompleted: true,
      });
    }

    setIsInterrupting(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (
      e.key === "Enter" &&
      !e.shiftKey &&
      !hasStarted &&
      description.trim() &&
      selectedFamily
    ) {
      e.preventDefault();
      onSubmit();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: "90vh",
          height: "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "16px 24px",
        }}
      >
        Try Playbook
        <IconButton onClick={onClose} size="small">
          <X size={20} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ padding: "24px", overflowY: "auto" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "24px",
          }}
        >
          {!hasStarted && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
              }}
            >
              <FamilySelect
                selectedFamily={selectedFamily}
                onChange={(f) => setSelectedFamily(f)}
                disabled={loading || hasStarted}
              />
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: "8px",
                  alignItems: "flex-end",
                }}
              >
                <TextField
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  onKeyPress={handleKeyPress}
                  label="Describe a task to test this playbook"
                  fullWidth
                  multiline
                  minRows={2}
                  maxRows={4}
                  disabled={loading || hasStarted || !selectedFamily}
                  autoFocus
                />
                <IconButton
                  onClick={onSubmit}
                  disabled={
                    loading ||
                    !description.trim() ||
                    !selectedFamily ||
                    hasStarted
                  }
                  sx={{
                    borderRadius: "30px",
                    background: "black",
                    "&:hover": {
                      background: "black",
                      opacity: 0.8,
                    },
                    "&:disabled": {
                      background: "black",
                      opacity: 0.5,
                    },
                    padding: "8px",
                  }}
                >
                  <ArrowUp size={26} color="white" />
                </IconButton>
              </Box>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ padding: "12px 24px" }}>
              Error starting agent.
            </Alert>
          )}

          {hasStarted && !hasActivated && <Loading />}

          {currentThreadRef && selectedFamily && (
            <TaskAgentDisplay
              familyRef={selectedFamily}
              agentThreadRef={currentThreadRef}
              stateManager={stateManager}
              onUpdatePlanDetails={handleUpdatePlanDetails}
              onFollowUpQuestion={handleFollowUpQuestion}
              onInterrupt={handleInterrupt}
              isInterrupting={isInterrupting}
            />
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};
